import { KeyboardEvent, ReactElement, useRef } from 'react';
import {
  Button,
  FormFieldState,
  RequestTypeBadge,
  SelectableCard,
  SelectField,
  StepIndicator,
} from '../../../../core/components';
import { tokens } from '../../../../core/theme';
import { REQUEST_TYPE_CONFIG, RequestType } from '../../../../core/types';
import {
  BUTTON_GROUP_GAP,
  DESKTOP_HEADER_GAP,
  FORM_MAX_WIDTH,
  MOBILE_HEADER_GAP,
  TOTAL_STEPS,
} from '../../constants';
import {
  useAutoFocusRef,
  ChannelFieldName,
  UseBranchOptionsResult,
  UseThemeOptionsResult,
} from '../../hooks';
import { Channel, CHANNEL_CONFIG, CHANNELS, isChannel } from '../../types/channel';
import { ChannelFormValues } from '../../types/channelForm';
import { useIsDesktop, usePageTitle } from '../../../../core/hooks';

const DESKTOP_FORM_GAP = 24;
const MOBILE_FORM_GAP = 37;
const SELECTION_GAP = 16;
const MOBILE_CARDS_GAP = 8;
const DESKTOP_CARDS_GAP = 24;
const MOBILE_HEADER_AND_CARDS_GAP = 16;
const DESKTOP_HEADER_AND_CARDS_GAP = 24;

type ChannelStepProps = {
  type: RequestType;
  values: ChannelFormValues;
  isValid: boolean;
  themeOptions: UseThemeOptionsResult;
  branchOptions: UseBranchOptionsResult;
  handleChannelSelect: (channel: Channel) => void;
  handleBranchSelect: (branchId: string) => void;
  handleThemeSelect: (themeId: string) => void;
  handleBlur: (field: ChannelFieldName) => void;
  getFieldState: (field: ChannelFieldName) => FormFieldState;
  getFieldError: (field: ChannelFieldName) => string | undefined;
  onBack: () => void;
  onContinue: () => void;
};

export const ChannelStep = ({
  type,
  values,
  isValid,
  themeOptions,
  branchOptions,
  handleChannelSelect,
  handleBranchSelect,
  handleThemeSelect,
  handleBlur,
  getFieldState,
  getFieldError,
  onBack,
  onContinue,
}: ChannelStepProps): ReactElement => {
  const isDesktop = useIsDesktop();
  const headingRef = useAutoFocusRef<HTMLHeadingElement>();
  const { icon: requestTypeIcon, label: requestTypeLabel } = REQUEST_TYPE_CONFIG[type];
  usePageTitle(`${requestTypeLabel} — Canal de atendimento`);

  const cardRefs = useRef<Record<Channel, HTMLButtonElement | null>>({
    loja_fisica: null,
    loja_online: null,
  });
  const activeChannel: Channel = isChannel(values.channel) ? values.channel : CHANNELS[0];

  const handleRadioGroupKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const currentIndex = CHANNELS.indexOf(activeChannel);
    let nextIndex: number | undefined;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % CHANNELS.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + CHANNELS.length) % CHANNELS.length;
    }
    if (nextIndex === undefined) {
      return;
    }
    event.preventDefault();
    const nextChannel = CHANNELS[nextIndex];
    handleChannelSelect(nextChannel);
    cardRefs.current[nextChannel]?.focus();
  };

  const showBranchField = values.channel !== 'loja_online';

  return (
    <div style={styles.page}>
      <div style={{ ...styles.form, gap: isDesktop ? DESKTOP_FORM_GAP : MOBILE_FORM_GAP }}>
        <div style={styles.selection}>
          <div
            style={{
              ...styles.headerAndCards,
              gap: isDesktop ? DESKTOP_HEADER_AND_CARDS_GAP : MOBILE_HEADER_AND_CARDS_GAP,
            }}
          >
            <div
              style={{ ...styles.header, gap: isDesktop ? DESKTOP_HEADER_GAP : MOBILE_HEADER_GAP }}
            >
              <div style={styles.badgeRow}>
                <RequestTypeBadge icon={requestTypeIcon} label={requestTypeLabel} />
                <StepIndicator currentStep={2} totalSteps={TOTAL_STEPS} />
              </div>
              <h1 ref={headingRef} tabIndex={-1} style={styles.sectionTitle}>
                Por qual canal foi sua experiência?
              </h1>
            </div>
            <div
              role="radiogroup"
              aria-label="Canal de atendimento"
              onKeyDown={handleRadioGroupKeyDown}
              style={{ ...styles.cardsRow, gap: isDesktop ? DESKTOP_CARDS_GAP : MOBILE_CARDS_GAP }}
            >
              {CHANNELS.map((channel) => (
                <SelectableCard
                  key={channel}
                  ref={(node) => {
                    cardRefs.current[channel] = node;
                  }}
                  variant="radio"
                  icon={CHANNEL_CONFIG[channel].icon}
                  title={CHANNEL_CONFIG[channel].title}
                  description={CHANNEL_CONFIG[channel].description}
                  isSelected={values.channel === channel}
                  tabIndex={activeChannel === channel ? 0 : -1}
                  onSelect={() => handleChannelSelect(channel)}
                />
              ))}
            </div>
          </div>
          {showBranchField && (
            <SelectField
              id="branch"
              label="Loja"
              placeholder="Escolha qual a loja de Ferreira Costa"
              value={values.branchId}
              options={branchOptions.options}
              state={branchOptions.error ? 'error' : getFieldState('branchId')}
              error={branchOptions.error ?? getFieldError('branchId')}
              loading={branchOptions.loading}
              onChange={handleBranchSelect}
              onBlur={() => handleBlur('branchId')}
            />
          )}
          <div style={styles.divider} />
          <SelectField
            id="theme"
            label="Tema"
            placeholder="Selecione um de nossos serviços"
            value={values.themeId}
            options={themeOptions.options}
            state={themeOptions.error ? 'error' : getFieldState('themeId')}
            error={themeOptions.error ?? getFieldError('themeId')}
            loading={themeOptions.loading}
            onChange={handleThemeSelect}
            onBlur={() => handleBlur('themeId')}
          />
        </div>
        <div style={styles.buttonGroup}>
          <Button variant="secondary" onClick={onBack}>
            Voltar
          </Button>
          <Button variant="primary" disabled={!isValid} onClick={onContinue}>
            Continuar
          </Button>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    padding: `${tokens.spacing.default} ${tokens.spacing.pageGutter}`,
    paddingBottom: tokens.spacing.large,
    boxSizing: 'border-box',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: FORM_MAX_WIDTH,
    boxSizing: 'border-box',
  },
  selection: {
    display: 'flex',
    flexDirection: 'column',
    gap: SELECTION_GAP,
    width: '100%',
  },
  headerAndCards: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  badgeRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  sectionTitle: {
    margin: tokens.spacing.none,
    fontSize: tokens.typography.fontSize.sectionTitle,
    fontWeight: tokens.typography.fontWeight.semiBold,
    color: tokens.colors.brand.gray.DEFAULT,
    textTransform: 'uppercase',
  },
  cardsRow: {
    display: 'flex',
    width: '100%',
  },
  divider: {
    height: 1,
    width: '100%',
    backgroundColor: tokens.colors.brand.gray.light850,
  },
  buttonGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: BUTTON_GROUP_GAP,
    width: '100%',
  },
};
